/**
 * Modèles académiques en DONNÉES (écart M3-P0-5 du programme de supériorité).
 *
 * Un modèle (échelle de notes, périodes de l'année, crédits, mentions, vocabulaire) est un
 * gabarit versionné du registre `academic_model_templates` ; le tenant en choisit un
 * (`tenant_academic_models`, version et copie figées). Aucun modèle, aucune échelle ni
 * aucune mention d'un pays n'est écrit dans le code : ajouter un préréglage = ajouter une
 * ligne de données.
 */
import { supabase, getActiveTenantId } from './supabase';
import { texteLocalise } from '../utils/classificationNotes';

export { texteLocalise };

/** Dernière version active de chaque gabarit, triée par code. */
export async function listerGabarits() {
  const { data, error } = await supabase
    .from('academic_model_templates')
    .select('code, version, statut, libelle, description, modele')
    .eq('actif', true)
    .eq('repli_sans_choix', false) // le repli de lecture n'est jamais propose au choix
    .order('code')
    .order('version', { ascending: false });
  if (error) throw error;
  const vus = new Set();
  return (data || []).filter((g) => (vus.has(g.code) ? false : (vus.add(g.code), true)));
}

/** Vocabulaire d'un gabarit dans la langue demandée (repli : français, anglais, première langue portée). */
export function vocabulaireDuGabarit(modele, langue = 'fr') {
  const t = modele?.terms || {};
  return t[langue] || t.fr || t.en || Object.values(t)[0] || {};
}

/**
 * Réglages vivants du tenant (ceux que lit le reste de l'application) déduits d'un gabarit.
 * Même recopie que la RPC `appliquer_modele_academique`, pour préremplir le formulaire.
 */
export function reglagesDuGabarit(modele, langue = 'fr') {
  const c = modele?.credit_system || {};
  return {
    grading_system: { ...(modele?.grading_system || {}) },
    credit_system: { ...c, semester: c.per_period ?? c.semester, year: c.per_year ?? c.year },
    attendance_threshold: modele?.attendance_threshold ?? null,
    terms: vocabulaireDuGabarit(modele, langue),
  };
}

/**
 * Enregistre le choix du tenant. `reglages: true` recopie aussi l'échelle, les crédits et le
 * vocabulaire sur les réglages vivants (côté serveur, sans écraser les clés propres du tenant) ;
 * `false` n'enregistre que le choix (l'écran écrit lui-même les réglages ajustés par l'utilisateur).
 */
export async function appliquerGabarit({ code, version = null, langue = null, reglages = true }) {
  const { data, error } = await supabase.rpc('appliquer_modele_academique', {
    p_code: code, p_version: version, p_langue: langue, p_reglages: reglages,
  });
  if (error) throw error;
  return data;
}

/**
 * Modèle EFFECTIF du tenant de la session (rendu par `modele_academique_effectif()`) : le modèle qu'il a
 * choisi, sinon le gabarit de repli, lu SANS écriture chez lui ; échelle et crédits suivent ses
 * réglages vivants. `origine` vaut `choix`, `repli` ou `aucun`.
 */
export async function modeleEffectif() {
  const { data, error } = await supabase.rpc('modele_academique_effectif');
  if (error) throw error;
  return data || null;
}

/** Modèle choisi par le tenant courant (null si aucun). */
export async function modeleChoisi() {
  let q = supabase
    .from('tenant_academic_models')
    .select('template_code, template_version, modele, langue, applique_le');
  const tenantId = getActiveTenantId?.();
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return data || null;
}

/**
 * Noms lisibles de l'échelle et des périodes d'un modèle, lus dans les référentiels ERDS
 * SYSTEMES_NOTATION et PERIODES_ACADEMIQUES (surcharges du tenant comprises) par les codes
 * `refs.echelle` et `periods.items[].ref` ; à défaut, le libellé propre au gabarit.
 */
export function resumeModele(modele, { echelles = [], periodes = [], langue = 'fr' } = {}) {
  const nom = (liste, code) => liste.find((i) => i.code === code)?.label || null;
  const items = modele?.periods?.items || [];
  return {
    echelle: nom(echelles, modele?.refs?.echelle) || null,
    periodes: items.map((it) => nom(periodes, it.ref) || texteLocalise(it.libelle, langue) || it.code),
  };
}
